import { createSuccessOperationResult } from '@hc-frontend/core-utils-business-operation';

import { detectGlobalPrivacyControl } from './detect-gpc';
import { isInStrictConsentState } from './is-in-strict-consent-state';
import type { ConsentStorageRepository, ShouldShowConsent } from './types';
import { ConsentStatus } from './types';

/**
 * Factory function that creates a function for determining if the consent banner should be shown
 *
 * The banner should be shown if:
 * - User is in a state requiring strict consent (e.g., California)
 * - User has not yet made a consent choice
 * - Global Privacy Control (GPC) is NOT enabled
 *
 * If GPC is enabled, the banner is hidden because the user has already signaled their
 * privacy preference through their browser.
 *
 * @param deps - Dependencies
 * @param deps.storage - Storage repository for persisting consent data
 * @returns A function that determines if the consent banner should be shown
 *
 * @example
 * ```typescript
 * const shouldShowConsent = shouldShowConsentFactory({
 *   storage: consentStorageAdapter
 * });
 * const result = await shouldShowConsent({ stateAcronym: 'CA' });
 * if (result.success && result.data) {
 *   // Show consent banner
 * }
 * ```
 */
export function shouldShowConsentFactory(deps: {
  storage: ConsentStorageRepository;
}): ShouldShowConsent {
  return async (params) => {
    // If user is not in a strict consent state, don't show banner
    if (!isInStrictConsentState(params)) {
      return createSuccessOperationResult(false);
    }

    // If GPC is enabled, don't show banner - user already signaled preference
    if (detectGlobalPrivacyControl()) {
      return createSuccessOperationResult(false);
    }

    // Check if user has already made a choice
    const status = deps.storage.getConsentStatus();

    // Show banner if user hasn't made a choice yet
    const shouldShow = status === ConsentStatus.UNKNOWN;

    return createSuccessOperationResult(shouldShow);
  };
}
